import type { MailingAddress, MailingAddressInput } from "@shopify/hydrogen-react/storefront-api-types";
import classNames from "classnames";
import { t } from "i18next";
import React, { useEffect, useMemo, useState } from "react";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";

import type { ISuggestion } from "@repo/shopify/hooks/useGooglePlacesAutocomplete";
import { useGooglePlacesAutocomplete } from "@repo/shopify/hooks/useGooglePlacesAutocomplete";
import { parseGooglePlaceAddress } from "@repo/shopify/utils/googlePlaces";

import Button from "@atoms/Button";
import Input from "@atoms/Input";
import Select from "@atoms/Select";

import AddressSuggestions from "@molecules/AddressSuggestions";

import useAddress from "@hooks/useAddress";
import useCheckLocation from "@hooks/useCheckLocation";
import useCustomerQuery from "@hooks/useCustomerQuery";
import useFormErrorHandler from "@hooks/useFormErrorHandler";

enum SubmitType {
  Create = "create",
  Edit = "edit",
}

interface IProps {
  address?: MailingAddress;
  isDefaultAddress?: boolean;
  onCompletedMutation?: (selectedAddressId?: string) => void | Promise<void>;
  className?: string;
  onClickCancel?: () => void;
  isEmptyAddress?: boolean;
}

interface IFormMailingAddress extends MailingAddressInput {
  countryCode: string;
  provinceCode: string;
}

export default function AddressFormIntl({
  address,
  isDefaultAddress = false,
  onClickCancel,
  isEmptyAddress = false,
  onCompletedMutation,
}: IProps) {
  const { setDefaultAddress, setDefaultAddressLoading, createAddress, createAddressLoading, editAddress, editAddressLoading } =
    useCustomerQuery();

  const { isCheckout } = useCheckLocation();

  /** 기본 주소 선택을 관리하는 state */
  const [checkedDefaultAddress, setCheckedDefaultAddress] = useState<boolean>(false);
  /** 폼 제출 진행 상태를 관리하는 state - 콜백 완료까지 */
  const [isProgress, setIsProgress] = useState<boolean>(false);

  const submitType = useMemo(() => (address?.id ? SubmitType.Edit : SubmitType.Create), [address]);

  const defaultValues: IFormMailingAddress = useMemo(() => {
    return {
      countryCode: address?.countryCodeV2 || "US",
      firstName: address?.firstName || "",
      lastName: address?.lastName || "",
      address1: address?.address1 || "",
      address2: address?.address2 || "",
      city: address?.city || "",
      company: "",
      phone: address?.phone || "",
      provinceCode: address?.provinceCode || "",
      province: address?.province || "",
      zip: address?.zip || "",
    };
  }, [address]);

  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue: setFormValue,
    formState: { errors },
    trigger,
    getValues,
    clearErrors,
  } = useForm<IFormMailingAddress>({ defaultValues });

  const watchedCountryCode = watch("countryCode");

  const { suggestions, fetchPlaceDetails, isSuggestionsVisible, setIsSuggestionsVisible, handleInputChange, clearSuggestions, clearCache } =
    useGooglePlacesAutocomplete({
      countryCode: watchedCountryCode,
    });

  const { countryOptions, stateOptions, formOrder } = useAddress({ countryCode: watchedCountryCode });

  const { onCommonError, displayError } = useFormErrorHandler({ values: getValues(), errors });

  /** 선택한 국가에 province 필드가 없는 경우 처리 */
  useEffect(() => {
    if (!formOrder.length) return;
    const hasProvince = formOrder?.flat().some((item) => item.key === "province");
    if (!hasProvince) {
      setFormValue("provinceCode", "-1");
      setFormValue("province", "");
    }
  }, [formOrder, setFormValue]);

  // provinceCode로 stateOptions에서 찾을 수 없으면 provinceName으로 찾기
  useEffect(() => {
    if (!address?.provinceCode || !stateOptions.length) return;

    const foundByCode = stateOptions.find((option) => option.value === address.provinceCode);
    if (!foundByCode && address?.province) {
      const foundByName = stateOptions.find((option) => option.name === address.province);
      if (foundByName) {
        setFormValue("provinceCode", foundByName.value);
        setFormValue("province", foundByName.name);
      }
    }
  }, [address, stateOptions, setFormValue]);

  /** 국가 변경 시 폼 에러 초기화 */
  useEffect(() => {
    clearErrors();
  }, [watchedCountryCode, clearErrors]);

  /** 사용자가 제안된 장소를 선택할 때 호출되며, 선택된 장소의 세부 정보 가져옴 */
  const handleSelect = (suggestion: ISuggestion) => async () => {
    clearSuggestions();
    const { mainText, place } = suggestion;

    // 장소의 세부 정보를 가져오기
    const addressComponents = await fetchPlaceDetails(place);
    if (!addressComponents) return;

    // Google Place 주소 파싱
    const parsedAddress = parseGooglePlaceAddress(addressComponents, stateOptions);

    // 폼 값 설정
    setFormValue("address1", mainText, { shouldValidate: true });
    setFormValue("city", parsedAddress.city || "", { shouldValidate: true });
    setFormValue("provinceCode", parsedAddress.provinceCode || "-1", { shouldValidate: true });
    setFormValue("province", parsedAddress.provinceName || "", { shouldValidate: true });
    setFormValue("zip", parsedAddress.zipCode || "", { shouldValidate: true });

    // 선택 후 세션 토큰 갱신
    clearCache();
  };

  const onSubmit: SubmitHandler<IFormMailingAddress> = async (formDataValue, event) => {
    if (!event) return;
    event?.preventDefault();
    if (createAddressLoading || editAddressLoading || setDefaultAddressLoading) return;

    try {
      setIsProgress(true);

      const { countryCode, provinceCode, province, ...restFormData } = formDataValue;
      const addressData: MailingAddressInput = {
        ...restFormData,
        country: countryCode,
        province: province || provinceCode,
      };

      if (submitType === SubmitType.Create) {
        await createAddress({ variables: { address: addressData } }).then(async ({ data: createData }) => {
          if (createData?.customerAddressCreate?.customerUserErrors.length) {
            return;
          }

          const checked = (checkedDefaultAddress || isEmptyAddress) ?? false;
          const newAddressId = createData?.customerAddressCreate?.customerAddress?.id || "";
          if (checked) {
            await setDefaultAddress({ variables: { addressId: newAddressId } });
          }
          await onCompletedMutation?.(newAddressId);
          onClickCancel?.();
        });
        return;
      }

      if (!address?.id) return;
      await editAddress({ variables: { address: addressData, id: address.id } }).then(async ({ data: editData }) => {
        if (editData?.customerAddressUpdate?.customerUserErrors.length) {
          return;
        }

        const checked = checkedDefaultAddress ?? false;
        if (checked) {
          await setDefaultAddress({
            variables: { addressId: editData?.customerAddressUpdate?.customerAddress?.id || "" },
          }).then(async () => {
            await onCompletedMutation?.();
            onClickCancel?.();
          });
        } else {
          await onCompletedMutation?.();
        }
        onClickCancel?.();
      });
    } finally {
      setIsProgress(false);
    }
  };

  return (
    <form className="flex flex-col" onSubmit={handleSubmit(onSubmit, onCommonError)}>
      <div className="col-span-8 flex flex-1 cursor-pointer flex-col gap-2.5">
        <Select
          as="form"
          id="country"
          label="Country"
          {...register("countryCode")}
          onChange={(e) => {
            setFormValue("countryCode", e.target.value);

            setFormValue("address1", "");
            setFormValue("address2", "");
            setFormValue("city", "");
            setFormValue("company", "");
            setFormValue("phone", "");
            setFormValue("provinceCode", "-1");
            setFormValue("province", "");
            setFormValue("zip", "");
          }}
          options={countryOptions}
        />
        {formOrder?.map((formRow) => {
          return formRow.map((form) => (
            <div
              className={classNames(
                formRow.length === 1 && "col-span-8",
                formRow.length === 3 && "pc:col-span-8 col-span-8",
                formRow.length === 2 && (form.key === "firstName" || form.key === "lastName") && "col-span-8",
                formRow.length === 2 && form.key !== "firstName" && form.key !== "lastName" && "pc:col-span-8 col-span-8",
                "relative",
              )}
              key={form.key}
            >
              {!form.key.endsWith("Name") && form.key !== "country" && form.key !== "province" && form.key !== "address1" && (
                <Input
                  disabled={isProgress}
                  id={form.key}
                  label={form.name}
                  placeholder={form.key === "zip" ? "-" : form.name}
                  type={form.name === "Phone" ? "tel" : "text"}
                  {...register(form.key, {
                    validate: (value) => {
                      if (form.required !== false) {
                        if (form.key === "address2") {
                          // 영어 알파벳, 숫자, 밑줄(_), 공백, 그리고 유니코드 구두점(쉼표, 마침표, 대시 등)만 허용
                          const nonEnglishRegex = /[^\w\s\p{P}]/u;
                          if (value && nonEnglishRegex.test(value)) {
                            return `${form.name} ${t(`error.invalid_english`)}`;
                          }
                          return true;
                        } else if (form.key === "zip") {
                          // 영문, 숫자, 대시(-), 언더바(_)만 허용
                          const validInputRegex = /^[a-zA-Z0-9-_ ]+$/;
                          if (!value?.trim()) return t(`error.necessary_${form.key}`);
                          if (value && !validInputRegex.test(value)) {
                            return t(`error.invalid_zip`);
                          }
                          return true;
                        } else if (form.key === "city") {
                          // 영어 알파벳, 숫자, 밑줄(_), 공백, 그리고 유니코드 구두점(쉼표, 마침표, 대시 등)만 허용
                          const nonEnglishRegex = /[^\w\s\p{P}]/u;
                          if (value && nonEnglishRegex.test(value)) {
                            return `${form.name} ${t(`error.invalid_english`)}`;
                          }
                        }
                        if (!value?.trim()) return t(`error.necessary_${form.key}`);
                      } else {
                        return true;
                      }
                    },
                    onChange: (e) => {
                      if (form.key === "country" || form.key === "province" || form.key === "address1") return;
                      if (form.key.endsWith("Name")) return;
                      setFormValue(form.key, e.target.value, { shouldValidate: true });
                    },
                  })}
                  errorMessage={displayError(form.key)?.errorMessage ?? ""}
                  isError={displayError(form.key)?.isError}
                />
              )}
              {form.key.endsWith("Name") && form.key !== "country" && form.key !== "province" && (
                <Controller
                  control={control}
                  key={form.key}
                  name={form.key}
                  render={({ field }) => (
                    <Input
                      {...field}
                      disabled={isProgress}
                      errorMessage={displayError(form.key)?.errorMessage ?? ""}
                      id={form.key}
                      isError={displayError(form.key)?.isError}
                      label={form.name}
                      onChange={(e) => {
                        if (form.key === "country" || form.key === "province") return;
                        trigger(form.key).then((isValid) => {
                          if (!isValid) {
                            const nonEnglishRegex = /[^\w\s\p{P}]/u;
                            if (nonEnglishRegex.test(e.target.value)) {
                              displayError(form.key);
                            }
                          }
                        });
                        setFormValue(form.key, e.target.value, { shouldValidate: true });
                        field.onChange(e);
                      }}
                      placeholder={form.name}
                      value={field.value || ""}
                    />
                  )}
                  rules={{
                    required: t(`error.necessary_${form.key}`),
                    validate: (value) => {
                      if (!value?.trim()) {
                        return t(`error.necessary_${form.key}`);
                      }
                      // 영문, 숫자, 공백, 콤마(,), 점(.), 대시(-) 및 공백도 허용
                      const nonEnglishRegex = /^[a-zA-Z0-9\s,.\-]+$/;
                      if (!nonEnglishRegex.test(value)) {
                        return `${form.name} ${t(`error.invalid_english`)}`;
                      }
                      return true;
                    },
                  }}
                />
              )}
              {form.key === "province" && (
                <Select
                  as="form"
                  id="provinceCode"
                  label={form.name}
                  options={stateOptions}
                  {...register("provinceCode", {
                    validate: (value) => {
                      if (value === "-1") return t(`error.necessary_province`);
                      return true;
                    },
                  })}
                  onChange={(e) => {
                    setFormValue("provinceCode", e.target.value, { shouldValidate: true });
                    const matchedProvince = stateOptions.find((option) => option.value === e.target.value);
                    if (matchedProvince) {
                      setFormValue("province", matchedProvince.name, { shouldValidate: true });
                    }
                  }}
                />
              )}
              {form.key === "address1" && (
                <React.Fragment key={form.key}>
                  <Controller
                    control={control}
                    name={form.key}
                    render={({ field }) => (
                      <Input
                        {...field}
                        autoComplete="new-password"
                        errorMessage={displayError(form.key)?.errorMessage ?? ""}
                        isError={displayError(form.key)?.isError}
                        label={form.name}
                        onBlur={() => {
                          setIsSuggestionsVisible(false);
                        }}
                        onChange={(e) => {
                          clearSuggestions();
                          clearErrors("address1");
                          trigger("address1").then((isValid) => {
                            if (!isValid) {
                              const nonEnglishRegex = /[^\w\s\p{P}]/u;
                              if (nonEnglishRegex.test(e.target.value)) {
                                displayError(form.key);
                              }
                            }
                          });
                          handleInputChange(e.target.value);
                          setFormValue("address1", e.target.value, { shouldValidate: true });
                          field.onChange(e);
                        }}
                        onFocus={() => {
                          setIsSuggestionsVisible(true);
                        }}
                        placeholder={form.name}
                        value={getValues("address1") || ""}
                      />
                    )}
                    rules={{
                      required: t(`error.necessary_${form.key}`),
                      validate: (value) => {
                        if (!value?.trim()) {
                          return t(`error.necessary_${form.key}`);
                        }
                        const nonEnglishRegex = /[^\w\s\p{P}]/u;
                        if (nonEnglishRegex.test(value)) {
                          return `${form.name} ${t(`error.invalid_english`)}`;
                        }
                        return true;
                      },
                    }}
                  />
                  {isSuggestionsVisible && suggestions.length !== 0 ? (
                    <AddressSuggestions onSelect={handleSelect} suggestions={suggestions} />
                  ) : null}
                </React.Fragment>
              )}
            </div>
          ));
        })}
      </div>
      <div className="pc:gap-2.5 pc:pt-4 absolute bottom-0 left-0 col-span-8 flex w-full shrink-0 cursor-pointer flex-col gap-5 bg-white p-4 pt-5">
        {!isDefaultAddress && (
          <Input
            checked={checkedDefaultAddress}
            disabled={isProgress}
            id="defaultAddress"
            label="Set as Default Address"
            name="defaultAddress"
            onChange={() => {
              setCheckedDefaultAddress((prev) => !prev);
            }}
            type="checkbox"
          />
        )}
        <Button as="square" disabled={isProgress} loading={isProgress} type="submit">
          {t("addresses.save")}
        </Button>
      </div>
    </form>
  );
}
